import React, { FC, useCallback, useState } from "react";
import EChartsReact from "echarts-for-react";
import { trafficLight } from "../../theme";
import { IDamsList, IStateOfKnowledge, ISocietalRisk } from "../../types/types";
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Accordion,
    AccordionSummary, ClickAwayListener,
    Divider, Grow, IconButton, MenuItem, Drawer,
    Paper, Popper, Toolbar, Grid, Tooltip
} from "@material-ui/core";
import { color } from "echarts";
import { CSVLink, CSVDownload } from "react-csv";
import GetAppIcon from '@material-ui/icons/GetApp';
import NotesIcon from '@material-ui/icons/Notes';

import { toPng } from "html-to-image";
import downloadjs from "downloadjs";

const useStyles = makeStyles({

    grid: {
        display: 'grid',

        gridTemplateColumns: 'max-content  repeat(16,1fr)',

        padding: '10px',
        justifyContent: 'center',

    },

    item: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
        padding: '0px 0px',
        borderBottom: '0.5px solid lightgrey',

    },
    item2: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        width: '15px',
        height: '15px',
        margin: '2px 0px',
        '&:hover': {
            transform: 'scale(1.0)',
            boxShadow: '0px 0px 5px 0px black',

        }
    },
    title: {
        display: 'flex',
        justifyContent: 'right',
        alignItems: 'center',
        borderBottom: '0px solid lightgrey',
        fontSize: '12px',
        padding: '0px 2px',

    },

    label: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderTop: '1px solid grey',
        borderLeft: '1px solid grey',
        borderBottom: '1px solid grey',

        fontSize: '12px',
        padding: '5px 5px',
        // writingMode: 'vertical-rl',
        // WebkitTransform: 'rotate(180deg)',
        // whiteSpace: 'nowrap',

    },


    label2: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderLeft: '1px solid grey',
        borderRight: '0px solid black',
        borderTop: '0px solid grey',
        padding: '10px 5px',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    overlap: {
        gridArea: '1 / 1 / 10 / 10',
        position: 'relative',
        ZIndex: '1',
    }

});

const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 14,
    },
}))(Tooltip);



interface IFMChartProps {
    stateOfKnowledge: IStateOfKnowledge[],
    damList: IDamsList[],
    risk_assessments: ISocietalRisk[]
}


const HeatMapTable: FC<IFMChartProps> = ({ stateOfKnowledge, damList, risk_assessments }) => {

    const classes = useStyles();

    let groups = Array.from(new Set(stateOfKnowledge.map(
        s => s.assessment_template_detail.template.group.name)))

    let subgroups: string[] = []

    groups.sort()
    console.log('groups', groups)

    groups.forEach(group => {

        let elements = stateOfKnowledge.filter(s => s.assessment_template_detail.template.group.name === group)

        let subs = Array.from(new Set(elements.map(
            s => s.assessment_template_detail.template.name))).sort()

        subgroups = [...subgroups, ...subs]

    })

    const assessment_ids = Array.from(new Set(stateOfKnowledge.map(
        s => s.assessment_project_id)))

    const dam_list = assessment_ids.map(a_id => {
        return damList.filter(dam => dam.id === a_id)[0].dam.name
    })

    let data = assessment_ids.map(a_id => {


        // get the societal level data for the dam
        let soc_level: any = risk_assessments.filter(dam => dam.assessment_project_id === a_id)

        // there is the potential for a dam to not have any data
        let societal_risk_value: number | string = 0
        if (soc_level.length > 0) {
            let societal_risk_value_number = soc_level[0].societal_risk_before
            // societal_risk_value = 
            societal_risk_value = societal_risk_value_number.toExponential(2)
        }


        const name = damList.filter(dam => dam.id === a_id)[0].dam.name

        const adequacy_data: any = []

        subgroups.map(subgroup => {

            const adequacy = stateOfKnowledge.filter(s => s.assessment_project_id === a_id).filter(s => s.assessment_template_detail.template.name === subgroup)

            if (adequacy[0]) {
                adequacy_data.push(adequacy[0].assessment_template_detail.adequacy.level)
            }
            else {
                adequacy_data.push(0)
            }

        })

        return [name, societal_risk_value, ...adequacy_data]

    })

    const handleCaptureClick = useCallback(() => {

        const pricingTableElmt = document.querySelector<HTMLElement>('.sok-table')
        if (!pricingTableElmt) return;
        toPng(pricingTableElmt, { pixelRatio: 4, cacheBust: true })
            .then((dataUrl: any) => {
                downloadjs(dataUrl, 'sok.png');
            })
            .catch((err) => {
                console.log(err)
            })
    }, [])


    let rows = (data.length)

    let societal_impact = data.map(d => d[1])

    //sort data by societal impact
    data.sort((a, b) => {
        return (b[1] * 1000) - (a[1] * 1000)
    })

    data.forEach(d => {
        if (d[1] === 0) {
            d[1] = 'Undefined'
        }
    })


    let csvData = [['dam name', 'societal impact', ...subgroups], ...data]

    const setColor = (level: number) => {

        if (level === 1) {
            return trafficLight.bgreen
        }
        if (level === 2) {
            return trafficLight.byellow
        }
        if (level === 3) {
            return trafficLight.bred
        }
        if (level === 0) {
            return trafficLight.grey
        }

    }

    const setName = (level: number) => {

        if (level === 1) {
            return 'Adequate'
        }
        if (level === 2) {
            return 'Questionable'
        }
        if (level === 3) {
            return 'Inadequate'
        }
        if (level === 0) {
            return 'Undefined'
        }
    }

    return (
        <>
            <div style={{ padding: "10px" }}>
                <Paper variant={"outlined"} className={'table-section.' + ' sok-table'}>
                    <Grid container>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={10}>
                            <p style={{ fontSize: "20px", position: 'relative', top: '-20px' }}><strong>State of Knowledge</strong></p>
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton aria-label="download" onClick={handleCaptureClick}>
                                <GetAppIcon />
                            </IconButton>
                            <CSVLink data={csvData} filename={"SoK.csv"}>
                                <IconButton aria-label="download" >
                                    <NotesIcon />
                                </IconButton>
                            </CSVLink>
                        </Grid>
                    </Grid>



                    <div className={classes.grid}>
                        <div style={{ gridRow: "1/span 2", gridColumn: "1/2" }}></div>
                        <div className={classes.label2} style={{ gridRow: "1/span 2", gridColumn: "2/3", borderLeft: "1px grey solid", borderRight: "0px red solid", borderTop: "1px solid grey", borderBottom: '1px solid grey' }} > Societal risk (live/year)</div>
                        <div className={classes.label2} style={{ gridColumn: "3/7", borderLeft: "1px grey solid", borderTop: "1px solid grey" }}>Consequences</div>
                        <div className={classes.label2} style={{ gridColumn: "7/12", borderTop: "1px solid grey" }}>Engineering inputs</div>
                        <div className={classes.label2} style={{ gridColumn: "12/14", borderTop: "1px solid grey" }}>Risk assessment</div>
                        <div className={classes.label2} style={{ gridColumn: "14/18", borderRight: "1px grey solid", borderTop: "1px solid grey" }}>Understanding of risk controls</div>



                        {

                            subgroups.map((subgroup, index) => {

                                if (index === 14) {
                                    return <div className={classes.label} style={{ borderRight: 'solid 1px grey' }}>{subgroup}</div>
                                }
                                else {
                                    return <div className={classes.label}>{subgroup}</div>
                                }

                            })
                        }

                        {

                            data.map((dam, ind) => {

                                let dam_name = ""
                                let top = ""
                                if (ind === 0) {
                                    top = "1px solid lightgrey"
                                }


                                const element = dam.map((d, index) => {

                                    let left = ""
                                    let right = ""

                                    const major = [6, 11, 13]
                                    major.includes(index) ? left = "1px solid grey" : left = ""

                                    if (index === 1) {
                                        left = "1px solid grey"
                                        right = ""
                                    }

                                    if (index === 2) {
                                        left = "1px solid grey"
                                    }

                                    if (index === 16) {
                                        right = "1px solid grey"
                                    }

                                    if (index === 0) {
                                        dam_name = d
                                        return <div className={classes.title} style={{}}>{d}</div>
                                    }
                                    if (index === 1) {
                                        return <div className={classes.item} style={{ borderLeft: left, borderRight: right, borderTop: top }} >{d}</div>
                                    }
                                    else {
                                        return (
                                            <LightTooltip title={
                                                <div>
                                                    <b>{dam_name}</b> <br /> {setName(d)}
                                                </div>
                                            }>
                                                <div className={classes.item} style={{ borderLeft: left, borderRight: right, borderTop: top }} >
                                                    <div className={classes.item2} style={{ backgroundColor: setColor(d), color: setColor(d) }}>
                                                        {d}
                                                    </div>

                                                </div>
                                            </LightTooltip>)

                                    }
                                })

                                return element
                            })
                        }




                    </div>
                </Paper>
            </div>



        </>

    )

}


export default HeatMapTable;